import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { simulationsAPI } from '../lib/api';
import { toast } from 'sonner';
import { Shield, Heart, Baby, AlertTriangle, CheckCircle, Info, Clock, Building2 } from 'lucide-react';

// Données de protection sociale selon le régime (mis à jour 2026 - Source: Previssima)
const REGIME_DATA = {
  liberal: {
    name: 'Profession libérale (BNC)',
    carence_maladie: 3,
    ij_delai: '3 jours de carence',
    organisme: 'CIPAV ou SSI (régime général)',
    specificites: [
      'Délai de carence de 3 jours (supprimé en cas de fausse couche)',
      'IJ après 1 an d\'affiliation minimum',
      'IJ versées tant que l\'arrêt de travail dure',
      'Régime général: IJ versées tant que l\'arrêt dure',
      'Retraite de base + complémentaire CIPAV',
      'Pas de couverture accidents du travail'
    ],
    couverture_base: {
      maladie: '70% du tarif de base',
      hospitalisation: '80% du tarif',
      maternite: '100% (après 10 mois d\'affiliation)',
      ij_maladie: 'Min 6,28€/jour - Max 65,84€/jour (2026)',
      invalidite: 'Pension selon revenus',
      deces: 'Capital décès limité'
    },
    info_ij: {
      formule: 'IJ = 1/730 × Revenu annuel moyen (3 dernières années)',
      seuil: 'Revenu min: 4 582€/an pour y avoir droit',
      plafond: 'Max 65,84€/jour même avec revenus élevés'
    }
  },
  artisan: {
    name: 'Artisan / Commerçant (BIC)',
    carence_maladie: 3,
    ij_delai: '3 jours de carence',
    organisme: 'SSI (Sécurité Sociale des Indépendants)',
    specificites: [
      'Délai de carence de 3 jours (supprimé en cas de fausse couche)',
      'IJ après 1 an d\'affiliation minimum',
      'IJ versées tant que l\'arrêt de travail dure',
      'Retraite de base + complémentaire SSI',
      'Possibilité de s\'assurer contre les AT/MP'
    ],
    couverture_base: {
      maladie: '70% du tarif de base',
      hospitalisation: '80% du tarif',
      maternite: '100%',
      ij_maladie: 'Min 6,28€/jour - Max 65,84€/jour (2026)',
      invalidite: 'Pension selon revenus',
      deces: 'Capital décès variable'
    },
    info_ij: {
      formule: 'IJ = 1/730 × Revenu annuel moyen (3 dernières années)',
      seuil: 'Revenu min: 4 582€/an pour y avoir droit',
      plafond: 'Max 65,84€/jour même avec revenus élevés'
    }
  }
};

const COMPARATIF_RETRAITE = {
  ae_25_ans: {
    ca_mensuel: 2000,
    duree: 25,
    trimestres: 100, // 4 par an max
    pension_mensuelle_estimee: { min: 400, max: 600 },
    explication: 'En auto-entrepreneur, les cotisations retraite sont proportionnelles au CA mais très faibles (environ 0.5% à 2.2% selon l\'activité).'
  },
  ei_25_ans: {
    ca_mensuel: 2000,
    duree: 25,
    trimestres: 100,
    pension_mensuelle_estimee: { min: 1200, max: 1500 },
    explication: 'En entreprise individuelle classique, les cotisations sont plus élevées (environ 25-30% du revenu), ce qui génère plus de droits à la retraite.'
  }
};

const ProtectionSocialePage = () => {
  const [simulating, setSimulating] = useState(false);
  const [formData, setFormData] = useState({
    chiffre_affaires_annuel: '',
    type_activite: 'BIC_SERVICE',
    situation_familiale: 'celibataire',
    regime: 'artisan'
  });
  const [result, setResult] = useState(null);
  const [activeTab, setActiveTab] = useState('simulateur');

  const handleSimulate = async (e) => {
    e.preventDefault();
    setSimulating(true);
    try {
      const response = await simulationsAPI.protectionSociale({
        chiffre_affaires_annuel: parseFloat(formData.chiffre_affaires_annuel),
        type_activite: formData.type_activite,
        situation_familiale: formData.situation_familiale
      });
      setResult({
        ...response.data,
        regime_data: REGIME_DATA[formData.regime] || REGIME_DATA.artisan
      });
    } catch (error) {
      toast.error('Erreur lors de la simulation');
    } finally {
      setSimulating(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  const selectedRegime = REGIME_DATA[formData.regime] || REGIME_DATA.artisan;

  return (
    <div className="space-y-6" data-testid="protection-sociale-page">
      <div>
        <h1 className="text-3xl font-bold font-['Manrope']">Protection sociale</h1>
        <p className="text-muted-foreground">Comprenez et optimisez votre couverture sociale selon votre régime</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="simulateur">Simulateur</TabsTrigger>
          <TabsTrigger value="comparatif">Comparatif régimes</TabsTrigger>
          <TabsTrigger value="retraite">Retraite AE vs EI</TabsTrigger>
        </TabsList>

        {/* Simulateur Tab */}
        <TabsContent value="simulateur">
          {/* Warning Card */}
          <Card className="border-amber-200 bg-amber-50 mb-6">
            <CardContent className="p-4 flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
              <div>
                <p className="font-medium text-amber-900">Attention : Protection limitée</p>
                <p className="text-sm text-amber-800">
                  En tant qu'auto-entrepreneur, votre protection sociale est minimale. 
                  Les délais de carence et les montants d'IJ varient selon votre régime (libéral vs artisan/commerçant).
                </p>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Simulateur */}
            <Card>
              <CardHeader>
                <CardTitle className="font-['Manrope'] flex items-center gap-2">
                  <Shield className="h-5 w-5" />
                  Simulateur de protection
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSimulate} className="space-y-6">
                  <div className="space-y-2">
                    <Label>Votre régime</Label>
                    <Select
                      value={formData.regime}
                      onValueChange={(value) => setFormData({ ...formData, regime: value })}
                    >
                      <SelectTrigger data-testid="protection-regime">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="liberal">Profession libérale (BNC)</SelectItem>
                        <SelectItem value="artisan">Artisan / Commerçant (BIC)</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      {selectedRegime.ij_delai} de délai pour les IJ
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label>Situation familiale</Label>
                    <Select
                      value={formData.situation_familiale}
                      onValueChange={(value) => setFormData({ ...formData, situation_familiale: value })}
                    >
                      <SelectTrigger data-testid="protection-situation">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="celibataire">Célibataire</SelectItem>
                        <SelectItem value="couple">En couple</SelectItem>
                        <SelectItem value="famille">Avec enfants</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Chiffre d'affaires annuel (€)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.chiffre_affaires_annuel}
                      onChange={(e) => setFormData({ ...formData, chiffre_affaires_annuel: e.target.value })}
                      placeholder="Ex: 30000"
                      required
                      data-testid="protection-ca"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full" 
                    disabled={simulating}
                    data-testid="protection-simulate"
                  >
                    {simulating ? 'Analyse...' : 'Analyser ma protection'}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Résultat */}
            <Card>
              <CardHeader>
                <CardTitle className="font-['Manrope']">Votre protection actuelle</CardTitle>
              </CardHeader>
              <CardContent>
                {result ? (
                  <div className="space-y-6">
                    {/* Régime info */}
                    <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
                      <div className="flex items-center gap-2 mb-2">
                        <Building2 className="h-4 w-4 text-primary" />
                        <span className="font-medium">{result.regime_data.name}</span>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Organisme : {result.regime_data.organisme}
                      </p>
                    </div>

                    {/* Délai IJ - IMPORTANT */}
                    <div className="p-4 rounded-lg border bg-amber-50 border-amber-200">
                      <div className="flex items-center gap-2 mb-2">
                        <Clock className="h-5 w-5 text-amber-600" />
                        <span className="font-medium text-amber-900">
                          Délai indemnités journalières
                        </span>
                      </div>
                      <p className="text-2xl font-bold font-['Manrope'] text-amber-600">
                        3 jours de carence
                      </p>
                      <p className="text-sm mt-1 text-amber-800">
                        {formData.regime === 'liberal' 
                          ? 'IJ versées après 3 jours de carence'
                          : '✓ Régime général: IJ versées tant que l\'arrêt dure'}
                      </p>
                      <p className="text-xs mt-2 text-amber-700">
                        Suppression du délai de carence en cas de fausse couche
                      </p>
                    </div>

                    {/* Cotisations */}
                    <div className="p-4 bg-muted/50 rounded-lg">
                      <p className="text-sm text-muted-foreground">Cotisations annuelles</p>
                      <p className="text-2xl font-bold font-['Manrope']">{formatCurrency(result.cotisation_annuelle)}</p>
                    </div>

                    {/* IJ */}
                    <div className="p-4 border rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <Heart className="h-4 w-4 text-primary" />
                        <span className="font-medium">Indemnités journalières maladie (2026)</span>
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-sm text-muted-foreground">Votre IJ/jour estimée</span>
                          <span className={`font-medium ${result.ij_maladie_par_jour === 0 ? 'text-red-600' : ''}`}>
                            {result.ij_maladie_par_jour === 0 ? 'Non éligible' : formatCurrency(result.ij_maladie_par_jour)}
                          </span>
                        </div>
                        <div className="flex justify-between text-xs text-muted-foreground">
                          <span>Min légal</span>
                          <span>6,28 €/jour</span>
                        </div>
                        <div className="flex justify-between text-xs text-muted-foreground">
                          <span>Max légal</span>
                          <span>65,84 €/jour</span>
                        </div>
                        <div className="flex justify-between pt-2 border-t">
                          <span className="text-sm text-muted-foreground">Allocation maternité</span>
                          <span className="font-medium">{formatCurrency(result.allocation_maternite)}</span>
                        </div>
                      </div>
                      <div className="mt-3 p-2 bg-blue-50 rounded text-xs text-blue-800">
                        <strong>Formule:</strong> IJ = 1/730 × Revenu annuel moyen (3 dernières années)<br/>
                        <strong>Condition:</strong> Revenu ≥ 4 582€/an pour y avoir droit
                      </div>
                    </div>

                    {/* Lacunes */}
                    <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                      <h4 className="font-medium text-red-900 mb-2 flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4" />
                        Lacunes identifiées
                      </h4>
                      <ul className="space-y-1">
                        {result.lacunes_identifiees.map((lacune, i) => (
                          <li key={`lacune-${i}`} className="text-sm text-red-800 flex items-center gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                            {lacune}
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Budget recommandé */}
                    <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
                      <p className="text-sm text-muted-foreground">Budget protection recommandé</p>
                      <p className="text-xl font-bold font-['Manrope'] text-primary">
                        {formatCurrency(result.budget_protection_recommande)}/an
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Soit ~{formatCurrency(result.budget_protection_recommande / 12)}/mois pour mutuelle + prévoyance
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="h-64 flex items-center justify-center text-muted-foreground">
                    <div className="text-center">
                      <Shield className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>Lancez une simulation pour analyser votre protection</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Comparatif régimes Tab */}
        <TabsContent value="comparatif">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {Object.entries(REGIME_DATA).map(([key, regime]) => (
              <Card key={key} className={key === 'liberal' ? 'border-red-200' : 'border-emerald-200'}>
                <CardHeader className={key === 'liberal' ? 'bg-red-50' : 'bg-emerald-50'}>
                  <CardTitle className="font-['Manrope'] flex items-center gap-2">
                    <Building2 className="h-5 w-5" />
                    {regime.name}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-6">
                  {/* Délai IJ */}
                  <div className={`p-4 rounded-lg ${key === 'liberal' ? 'bg-red-100' : 'bg-emerald-100'}`}>
                    <p className="text-sm font-medium mb-1">Délai indemnités journalières</p>
                    <p className={`text-3xl font-bold font-['Manrope'] ${key === 'liberal' ? 'text-red-600' : 'text-emerald-600'}`}>
                      {regime.ij_delai}
                    </p>
                  </div>

                  {/* Organisme */}
                  <div>
                    <p className="text-sm text-muted-foreground">Organisme de rattachement</p>
                    <p className="font-medium">{regime.organisme}</p>
                  </div>

                  {/* Couverture */}
                  <div>
                    <p className="font-medium mb-3">Couverture de base</p>
                    <div className="space-y-2">
                      {Object.entries(regime.couverture_base).map(([key, value]) => (
                        <div key={key} className="flex justify-between text-sm">
                          <span className="text-muted-foreground capitalize">{key.replace(/_/g, ' ')}</span>
                          <span>{value}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Spécificités */}
                  <div>
                    <p className="font-medium mb-3">Spécificités</p>
                    <ul className="space-y-2">
                      {regime.specificites.map((spec, i) => (
                        <li key={`conseil-${i}`} className="text-sm flex items-start gap-2">
                          <CheckCircle className={`h-4 w-4 mt-0.5 flex-shrink-0 ${key === 'liberal' ? 'text-red-500' : 'text-emerald-500'}`} />
                          <span>{spec}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Retraite comparatif Tab */}
        <TabsContent value="retraite">
          <Card className="border-red-200 bg-red-50 mb-6">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <AlertTriangle className="h-8 w-8 text-red-600 flex-shrink-0" />
                <div>
                  <h3 className="text-xl font-bold font-['Manrope'] text-red-900 mb-2">
                    La vérité sur votre retraite d'auto-entrepreneur
                  </h3>
                  <p className="text-red-800">
                    Avec un CA de 2 000 €/mois pendant 25 ans, voici ce que vous toucherez réellement à la retraite.
                    Comparez avec ce que vous auriez touché en entreprise individuelle classique.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Auto-entrepreneur */}
            <Card className="border-red-200">
              <CardHeader className="bg-red-50">
                <CardTitle className="font-['Manrope'] text-red-900">
                  Auto-entrepreneur
                </CardTitle>
                <p className="text-sm text-red-700">25 ans • 2 000 €/mois de CA</p>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                <div className="text-center p-6 bg-red-100 rounded-xl">
                  <p className="text-sm text-red-700 mb-2">Pension mensuelle estimée</p>
                  <p className="text-4xl font-bold font-['Manrope'] text-red-600">
                    {COMPARATIF_RETRAITE.ae_25_ans.pension_mensuelle_estimee.min} - {COMPARATIF_RETRAITE.ae_25_ans.pension_mensuelle_estimee.max} €/mois
                  </p>
                </div>

                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">CA mensuel moyen</span>
                    <span className="font-medium">{formatCurrency(COMPARATIF_RETRAITE.ae_25_ans.ca_mensuel)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Durée de cotisation</span>
                    <span className="font-medium">{COMPARATIF_RETRAITE.ae_25_ans.duree} ans</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Trimestres validés</span>
                    <span className="font-medium">~{COMPARATIF_RETRAITE.ae_25_ans.trimestres}</span>
                  </div>
                </div>

                <div className="p-4 bg-muted/50 rounded-lg">
                  <p className="text-sm text-muted-foreground">
                    {COMPARATIF_RETRAITE.ae_25_ans.explication}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Entreprise individuelle */}
            <Card className="border-emerald-200">
              <CardHeader className="bg-emerald-50">
                <CardTitle className="font-['Manrope'] text-emerald-900">
                  Entreprise individuelle classique
                </CardTitle>
                <p className="text-sm text-emerald-700">25 ans • Revenus équivalents</p>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                <div className="text-center p-6 bg-emerald-100 rounded-xl">
                  <p className="text-sm text-emerald-700 mb-2">Pension mensuelle estimée</p>
                  <p className="text-4xl font-bold font-['Manrope'] text-emerald-600">
                    {COMPARATIF_RETRAITE.ei_25_ans.pension_mensuelle_estimee.min} - {COMPARATIF_RETRAITE.ei_25_ans.pension_mensuelle_estimee.max} €/mois
                  </p>
                </div>

                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Revenu mensuel moyen</span>
                    <span className="font-medium">{formatCurrency(COMPARATIF_RETRAITE.ei_25_ans.ca_mensuel)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Durée de cotisation</span>
                    <span className="font-medium">{COMPARATIF_RETRAITE.ei_25_ans.duree} ans</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Trimestres validés</span>
                    <span className="font-medium">~{COMPARATIF_RETRAITE.ei_25_ans.trimestres}</span>
                  </div>
                </div>

                <div className="p-4 bg-muted/50 rounded-lg">
                  <p className="text-sm text-muted-foreground">
                    {COMPARATIF_RETRAITE.ei_25_ans.explication}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Différence */}
          <Card className="mt-6">
            <CardContent className="p-6">
              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-2">Différence de pension mensuelle</p>
                <p className="text-5xl font-bold font-['Manrope'] text-red-600 mb-4">
                  - 600 à 900 €/mois
                </p>
                <p className="text-muted-foreground max-w-2xl mx-auto">
                  Soit une perte de <strong>7 200 à 10 800 €/an</strong> à la retraite. 
                  Sur 20 ans de retraite, cela représente <strong>144 000 à 216 000 €</strong> de manque à gagner.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Solution */}
          <Card className="mt-6 border-primary">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Info className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-bold font-['Manrope'] mb-2">Comment compenser ce déficit ?</h3>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      Ouvrir un PER (Plan Épargne Retraite) déductible des impôts
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      Épargner 10 à 20% de vos revenus nets selon votre âge
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      Investir dans l'immobilier locatif pour des revenus complémentaires
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      Utiliser notre simulateur d'épargne retraite pour planifier
                    </li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Recommandations */}
      {result && activeTab === 'simulateur' && (
        <Card>
          <CardHeader>
            <CardTitle className="font-['Manrope'] flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-primary" />
              Recommandations pour votre régime
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {result.recommandations.map((reco, i) => (
                <div key={`option-${i}`} className="p-4 border rounded-lg">
                  <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center mb-3">
                    <span className="text-primary font-bold">{i + 1}</span>
                  </div>
                  <p className="text-sm">{reco}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ProtectionSocialePage;
